import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Calendar, Loader2, FileText, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { buildLogsAPI } from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';
import { usePageTitle } from '../hooks/usePageTitle';
import { usePageContent } from '../hooks/usePageContent';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  if (url.startsWith('/api')) return `${API_BASE_URL}${url}`;
  return `${API_BASE_URL}/api/uploads/image/${url}`;
};

const getYouTubeEmbedUrl = (url) => {
  if (!url) return null;
  const match = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{11})/);
  return match ? `https://www.youtube.com/embed/${match[1]}` : url;
};

// Helper to get localized content
const getLocalizedField = (item, field, language) => {
  if (language === 'tr') {
    const trField = item[`${field}_tr`];
    if (trField && trField.trim()) return trField;
  }
  return item[field];
};

const BuildLogs = () => {
  const [buildLogs, setBuildLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { t, language } = useLanguage();
  const { getContent } = usePageContent('buildlogs');
  
  // Dynamic page title
  usePageTitle('nav.buildLogs', 'Build Logs', 'Yapım Günlükleri');

  useEffect(() => {
    const fetchLogs = async () => {
      try {
        setLoading(true);
        const data = await buildLogsAPI.getAll();
        setBuildLogs(data);
      } catch (err) {
        setError(err.message);
        console.error('Failed to fetch build logs:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchLogs();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center" style={{ background: '#0F172A' }}>
        <Loader2 className="w-12 h-12 animate-spin" style={{ color: '#00F0FF' }} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center" style={{ background: '#0F172A' }}>
        <div className="text-center">
          <p className="body-large mb-4" style={{ color: '#F8FAFC' }}>{t('buildLogs.error.loadFailed')}</p>
          <p className="body-small" style={{ color: '#94A3B8' }}>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20" style={{ background: '#0F172A' }}>
      {/* Hero Section */}
      <section className="relative py-24 px-[7.6923%] overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('${getContent('hero_image') || 'https://images.unsplash.com/photo-1581092160607-ee22621dd758?w=1920'}')`
          }}
        >
          <div className="absolute inset-0" style={{
            background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.9) 0%, rgba(30, 41, 59, 0.85) 50%, rgba(0, 240, 255, 0.2) 100%)'
          }}></div>
        </div>
        <div className="relative z-10 max-w-[1400px] mx-auto text-center">
          <h1 className="display-huge mb-6" style={{
            color: '#F8FAFC',
            textShadow: '0 0 20px rgba(0, 240, 255, 0.5)'
          }}>{getContent('hero_title')}</h1>
          <p className="body-large max-w-3xl mx-auto" style={{ color: '#F8FAFC', opacity: 0.9 }}>
            {getContent('hero_subtitle')}
          </p>
        </div>
      </section>

      {/* Build Logs Grid */}
      <section className="py-24 px-[7.6923%]" style={{ background: '#0F172A' }}>
        <div className="max-w-[1400px] mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {buildLogs.map((log) => (
              <article 
                key={log.id}
                className="glass-card overflow-hidden dark-transition dark-hover"
                data-testid={`build-log-card-${log.id}`}
              >
                <div 
                  className="h-56 relative overflow-hidden"
                  style={{ background: 'rgba(0, 240, 255, 0.1)' }}
                >
                  {log.image ? (
                    <img 
                      src={getImageUrl(log.image)} 
                      alt={getLocalizedField(log, 'title', language)}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <FileText size={48} style={{ color: '#64748B' }} />
                    </div>
                  )}
                  <div className="absolute top-4 left-4 px-4 py-2 flex items-center gap-2" style={{
                    background: 'rgba(15, 23, 42, 0.9)',
                    backdropFilter: 'blur(8px)',
                    border: '1px solid rgba(216, 180, 254, 0.3)'
                  }}>
                    <Calendar size={16} style={{ color: '#D8B4FE' }} />
                    <span className="body-small" style={{
                      color: '#D8B4FE',
                      textShadow: '0 0 8px rgba(216, 180, 254, 0.4)'
                    }}>{log.date}</span>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="heading-2 mb-3" style={{ color: '#F8FAFC' }}>
                    {getLocalizedField(log, 'title', language)}
                  </h3>
                  <p className="body-small leading-relaxed mb-4" style={{ color: '#F8FAFC', opacity: 0.85 }}>
                    {getLocalizedField(log, 'excerpt', language)}
                  </p>
                  <Link 
                    to={`/build-logs/${log.id}`}
                    className="inline-flex w-full justify-center items-center gap-2 px-6 py-3 font-semibold text-base uppercase transition-all duration-300"
                    data-testid={`read-log-btn-${log.id}`}
                    style={{
                      background: 'linear-gradient(90deg, #00F0FF 0%, #0F172A 100%)',
                      color: '#F8FAFC',
                      border: '2px solid #00F0FF',
                      boxShadow: '0 0 20px rgba(0, 240, 255, 0.3)',
                      letterSpacing: '0.5px'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.background = 'transparent';
                      e.currentTarget.style.boxShadow = '0 0 30px rgba(0, 240, 255, 0.6), 0 0 60px rgba(0, 240, 255, 0.4)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.background = 'linear-gradient(90deg, #00F0FF 0%, #0F172A 100%)';
                      e.currentTarget.style.boxShadow = '0 0 20px rgba(0, 240, 255, 0.3)';
                    }}
                  >
                    {t('buildLogs.readMore')}
                  </Link>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export const BuildLogDetail = () => {
  const { id } = useParams();
  const [log, setLog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const { t, language } = useLanguage();

  const galleryImages = (log?.images || []).filter(Boolean);

  useEffect(() => {
    const handleKey = (e) => {
      if (lightboxIndex === null) return;
      if (e.key === 'Escape') setLightboxIndex(null);
      if (e.key === 'ArrowRight') setLightboxIndex((i) => (i + 1) % galleryImages.length);
      if (e.key === 'ArrowLeft') setLightboxIndex((i) => (i - 1 + galleryImages.length) % galleryImages.length);
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [lightboxIndex, galleryImages.length]);
  
  // Dynamic page title - will update when log is loaded
  usePageTitle('nav.buildLogs', 'Build Logs', 'Yapım Günlükleri');

  useEffect(() => {
    const fetchLog = async () => {
      try {
        setLoading(true);
        const data = await buildLogsAPI.getById(id);
        setLog(data);
        // Update title with log title
        const logTitle = language === 'tr' && data.title_tr ? data.title_tr : data.title;
        document.title = `${logTitle} | Kartal Air`;
      } catch (err) {
        setError(err.message);
        console.error('Failed to fetch build log:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchLog();
  }, [id, language]);

  if (loading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center" style={{ background: '#0F172A' }}>
        <Loader2 className="w-12 h-12 animate-spin" style={{ color: '#00F0FF' }} />
      </div>
    );
  }

  if (error || !log) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center" style={{ background: '#0F172A' }}>
        <div className="text-center">
          <h1 className="display-large mb-4" style={{ color: '#F8FAFC' }}>{t('buildLogs.notFound')}</h1>
          <Link to="/build-logs" className="btn-primary inline-flex" data-testid="back-to-logs-btn">
            <ArrowLeft size={20} />
            {t('buildLogs.backToLogs')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20" style={{ background: '#0F172A' }} data-testid="build-log-detail">
      {/* Hero Image */}
      <section 
        className="relative h-[60vh] overflow-hidden"
      >
        {log.image ? (
          <img 
            src={getImageUrl(log.image)} 
            alt={getLocalizedField(log, 'title', language)}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center" style={{ background: 'rgba(30, 41, 59, 0.6)' }}>
            <FileText size={96} style={{ color: '#64748B' }} />
          </div>
        )}
        <div className="absolute inset-0" style={{
          background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.85) 0%, rgba(30, 41, 59, 0.7) 50%, rgba(0, 240, 255, 0.3) 100%)'
        }}></div>
        <div className="relative z-10 max-w-[1400px] mx-auto px-[7.6923%] h-full flex items-end pb-12">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <Calendar size={20} style={{ color: '#D8B4FE' }} />
              <span className="body-medium" style={{
                color: '#D8B4FE',
                textShadow: '0 0 10px rgba(216, 180, 254, 0.5)'
              }}>{log.date}</span>
            </div>
            <h1 className="display-large" style={{
              color: '#F8FAFC',
              textShadow: '0 0 20px rgba(0, 240, 255, 0.5)'
            }}>{getLocalizedField(log, 'title', language)}</h1>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-24 px-[7.6923%]" style={{ background: '#0F172A' }}>
        <div className="max-w-4xl mx-auto">
          <Link 
            to="/build-logs"
            className="inline-flex items-center gap-2 mb-8 transition-colors duration-300"
            style={{ color: '#00F0FF' }}
            onMouseOver={(e) => e.currentTarget.style.color = '#06B6D4'}
            onMouseOut={(e) => e.currentTarget.style.color = '#00F0FF'}
          >
            <ArrowLeft size={20} />
            <span className="body-medium">{t('buildLogs.backToLogs')}</span>
          </Link>

          <div className="glass-card p-12">
            <p className="body-large leading-relaxed whitespace-pre-line" style={{ color: '#F8FAFC', opacity: 0.9 }}>
              {getLocalizedField(log, 'content', language)}
            </p>
          </div>

          {/* Video Section */}
          {(log.video_url || log.video_file) && (
            <div className="mt-12" data-testid="build-log-video-section">
              <h2 className="heading-2 mb-6" style={{ color: '#F8FAFC', textShadow: '0 0 15px rgba(0, 240, 255, 0.4)' }}>
                {language === 'tr' ? 'Video' : 'Video'}
              </h2>
              <div className={log.video_orientation === 'portrait' ? 'flex justify-center' : ''}>
                <div
                  className={`glass-card overflow-hidden ${log.video_orientation === 'portrait' ? 'aspect-[9/16] w-full max-w-sm' : 'aspect-video w-full'}`}
                  style={{ border: '1px solid rgba(0, 240, 255, 0.25)', boxShadow: '0 0 30px rgba(0, 240, 255, 0.15)' }}
                >
                  {log.video_url ? (
                    <iframe
                      src={getYouTubeEmbedUrl(log.video_url)}
                      title={getLocalizedField(log, 'title', language)}
                      className="w-full h-full"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      data-testid="build-log-video-embed"
                    />
                  ) : (
                    <video
                      src={log.video_file}
                      controls
                      playsInline
                      className="w-full h-full object-contain"
                      style={{ background: '#000' }}
                      data-testid="build-log-video-player"
                    />
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Image Gallery Section */}
          {galleryImages.length > 0 && (
            <div className="mt-12" data-testid="build-log-gallery-section">
              <h2 className="heading-2 mb-6" style={{ color: '#F8FAFC', textShadow: '0 0 15px rgba(0, 240, 255, 0.4)' }}>
                {language === 'tr' ? 'Fotoğraf Galerisi' : 'Photo Gallery'}
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {galleryImages.map((img, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => setLightboxIndex(idx)}
                    className="group relative aspect-[4/3] overflow-hidden glass-card cursor-pointer"
                    style={{ border: '1px solid rgba(0, 240, 255, 0.2)' }}
                    data-testid={`build-log-gallery-image-${idx}`}
                  >
                    <img
                      src={getImageUrl(img)}
                      alt={`${getLocalizedField(log, 'title', language)} - ${idx + 1}`}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center" style={{ background: 'rgba(15, 23, 42, 0.5)' }}>
                      <span className="body-small px-3 py-1" style={{ color: '#00F0FF', border: '1px solid rgba(0, 240, 255, 0.5)', background: 'rgba(15, 23, 42, 0.8)' }}>
                        {language === 'tr' ? 'Büyüt' : 'Enlarge'}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Lightbox */}
      {lightboxIndex !== null && galleryImages[lightboxIndex] && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          style={{ background: 'rgba(15, 23, 42, 0.95)', backdropFilter: 'blur(12px)' }}
          onClick={() => setLightboxIndex(null)}
          data-testid="build-log-lightbox"
        >
          <button
            type="button"
            onClick={() => setLightboxIndex(null)}
            className="absolute top-6 right-6 p-2 transition-colors duration-300 z-10"
            style={{ color: '#F8FAFC' }}
            data-testid="lightbox-close-btn"
            aria-label="Close"
          >
            <X size={32} />
          </button>
          {galleryImages.length > 1 && (
            <>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setLightboxIndex((lightboxIndex - 1 + galleryImages.length) % galleryImages.length); }}
                className="absolute left-4 md:left-8 p-2 z-10"
                style={{ color: '#00F0FF' }}
                data-testid="lightbox-prev-btn"
                aria-label="Previous"
              >
                <ChevronLeft size={40} />
              </button>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setLightboxIndex((lightboxIndex + 1) % galleryImages.length); }}
                className="absolute right-4 md:right-8 p-2 z-10"
                style={{ color: '#00F0FF' }}
                data-testid="lightbox-next-btn"
                aria-label="Next"
              >
                <ChevronRight size={40} />
              </button>
            </>
          )}
          <img
            src={getImageUrl(galleryImages[lightboxIndex])}
            alt={`${getLocalizedField(log, 'title', language)} - ${lightboxIndex + 1}`}
            className="max-w-full max-h-[85vh] object-contain"
            style={{ boxShadow: '0 0 60px rgba(0, 240, 255, 0.2)' }}
            onClick={(e) => e.stopPropagation()}
          />
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 body-small px-4 py-2" style={{ color: '#F8FAFC', background: 'rgba(15, 23, 42, 0.8)', border: '1px solid rgba(0, 240, 255, 0.3)' }}>
            {lightboxIndex + 1} / {galleryImages.length}
          </div>
        </div>
      )}
    </div>
  );
};

export default BuildLogs;
